import { Component, ChangeDetectionStrategy, signal, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { TypeSubtypeItemRowComponent } from '../type-subtype-item-row/type-subtype-item-row.component';
import {
  ServiceTypeConfig,
  SourceMapping,
  TICKET_SOURCES,
  TicketSource,
  createEmptyServiceTypeConfig,
} from '../../../../models/service-type-mapping.models';

/**
 * Dialog component for adding/editing a source-specific mapping.
 *
 * Contains:
 * - Dropdown to select ticket source (Cyflare-managed list)
 * - Type/Sub-Type/Item selection row
 */
@Component({
  selector: 'app-source-mapping-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    LabelModule,
    DropDownsModule,
    TypeSubtypeItemRowComponent,
  ],
  templateUrl: './source-mapping-dialog.component.html',
  styleUrls: ['./source-mapping-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SourceMappingDialogComponent {
  // Inputs
  organizationId = input<number | null>(null);
  boardId = input<number | null>(null);
  existingMapping = input<SourceMapping | null>(null);
  usedSources = input<string[]>([]);

  // Outputs
  mappingSaved = output<SourceMapping>();
  dialogClosed = output<void>();

  // Internal state
  selectedSource = signal<TicketSource | null>(null);
  currentConfig = signal<ServiceTypeConfig>(createEmptyServiceTypeConfig());

  // Computed: available sources (exclude already-used ones, except current editing)
  availableSources = computed(() => {
    const used = this.usedSources();
    const existing = this.existingMapping();

    return TICKET_SOURCES.filter((source) => {
      // Always include the source being edited
      if (existing && source === existing.sourceValue) {
        return true;
      }
      // Exclude already-used sources
      return !used.includes(source);
    });
  });

  // Computed: dialog title
  dialogTitle = computed(() => {
    return this.existingMapping() ? 'Edit Source Mapping' : 'Add Source Mapping';
  });

  // Computed: is save enabled
  isSaveEnabled = computed(() => {
    // Must have a source selected
    return this.selectedSource() !== null;
  });

  constructor() {
    // Initialize from existing mapping if editing
    const existing = this.existingMapping();
    if (existing) {
      this.selectedSource.set(existing.sourceValue as TicketSource);
      this.currentConfig.set({ ...existing.config });
    }
  }

  /**
   * Initialize component when inputs change
   */
  ngOnInit(): void {
    const existing = this.existingMapping();
    if (existing) {
      this.selectedSource.set(existing.sourceValue as TicketSource);
      this.currentConfig.set({ ...existing.config });
    }
  }

  /**
   * Handle source selection change
   */
  onSourceChange(source: TicketSource | null): void {
    this.selectedSource.set(source);
  }

  /**
   * Handle config change from TypeSubtypeItemRowComponent
   */
  onConfigChanged(config: ServiceTypeConfig): void {
    this.currentConfig.set(config);
  }

  /**
   * Save the mapping
   */
  save(): void {
    const source = this.selectedSource();
    if (!source) return;

    const mapping: SourceMapping = {
      id: this.existingMapping()?.id,
      sourceValue: source,
      config: this.currentConfig(),
    };

    this.mappingSaved.emit(mapping);
  }

  /**
   * Close the dialog
   */
  close(): void {
    this.dialogClosed.emit();
  }
}
